import { useCallback, useRef, useState } from 'react'

/**
 * A stable ref-callback paired with reactive state for a DOM element.
 *
 * Use this when a parent needs both the live element (e.g. to pass into a
 * portal `container` prop, IntersectionObserver, ResizeObserver, etc.) AND
 * to re-render children when the element becomes available.
 *
 * Why not just `ref={(el) => setEl(el)}` inline? React's contract for ref
 * callbacks is to call the OLD callback with `null` (cleanup) and the NEW
 * callback with the current node whenever the callback's identity changes.
 * Inline arrow functions are a fresh identity every render, so React fires
 * `setEl(null)` then `setEl(node)` on EVERY parent re-render. The transient
 * `null` flash propagates down through any context whose value is the
 * element, and any consumer of that context (e.g. a Radix `<Portal
 * container={...}>`) tears down and rebuilds its subtree. This was the
 * cause of the user-reported flicker on every scanner alert in 2026-05-01:
 * AppLayout subscribed to `s.alerts.events` via `useAlertEffects`, every
 * alert re-rendered AppLayout, the inline ref callback was recreated, the
 * `mainContentEl` state churned `el → null → el`, and every portaled Sheet
 * (column picker, filter picker, mobile sidebar) unmounted and remounted.
 *
 * The hook captures the fix as a one-liner — `useCallback` with `[]` deps
 * makes the callback identity stable, so React never fires the cleanup,
 * and `setEl((prev) => prev !== node ? node : prev)` skips state updates
 * when the element didn't actually change.
 */
export function useRefState<T extends Element = Element>() {
  const ref = useRef<T | null>(null)
  const [el, setEl] = useState<T | null>(null)
  const setRef = useCallback((node: T | null) => {
    ref.current = node
    setEl((prev) => (prev !== node ? node : prev))
  }, [])
  return [setRef, el, ref] as const
}
